import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Select, SelectItem, Tag, TextInput } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { MoneyInput } from '../components/MoneyInput';

// Ban Ca ktf_1990 (config `ban_ca` tren he A, backend/heB/games/banca/cau_hinh.js). O de trong =
// game dung mac dinh (backend/config/ban_ca.json); chi o co gia tri moi ghi vao CMS.
type Room = { rtp?: number; mucCuoc?: number[] };
type Doc = { phong?: { 1?: Room; 3?: Room }; hu?: { phanTramVaoHu?: number; huKhoiTao?: number; heSoNo?: number }; bot?: { bat?: boolean; soBot?: [number, number] } };
type Last = { id: string; status: string; createdAt: string; lastError: string | null; chiTiet: string | null };
type Data = { noiDung: Doc | null; hieuLuc: Doc | null; meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string; createdAt: string }[]; last: Last | null };
type RoomForm = { rtp: string; muc: string };
type Form = { r1: RoomForm; r3: RoomForm; phanTram: string; khoiTao: string; heSoNo: string; botBat: '' | 'bat' | 'tat'; botMin: string; botMax: string };

const ROOMS = [
  { key: 'r1', id: 1, name: 'Săn Cá Biển Sâu', note: 'Phòng thường.' },
  { key: 'r3', id: 3, name: 'Hải Vực Cự Thú', note: 'Phòng VIP, có hũ Godzilla: phần góp hũ trừ vào tỷ lệ trả.' },
] as const;

const pctOf = (x: number) => String(Number((x * 100).toFixed(2)));
const mucText = (m: number[]) => m.map((x) => formatVnd(x)).join(', ');
const toForm = (d: Doc | null): Form => {
  const room = (r?: Room): RoomForm => ({ rtp: r?.rtp != null ? pctOf(r.rtp) : '', muc: r?.mucCuoc ? mucText(r.mucCuoc) : '' });
  return {
    r1: room(d?.phong?.[1]), r3: room(d?.phong?.[3]),
    phanTram: d?.hu?.phanTramVaoHu != null ? String(d.hu.phanTramVaoHu) : '',
    khoiTao: d?.hu?.huKhoiTao != null ? String(d.hu.huKhoiTao) : '',
    heSoNo: d?.hu?.heSoNo != null ? String(d.hu.heSoNo) : '',
    botBat: d?.bot?.bat == null ? '' : d.bot.bat ? 'bat' : 'tat',
    botMin: d?.bot?.soBot ? String(d.bot.soBot[0]) : '', botMax: d?.bot?.soBot ? String(d.bot.soBot[1]) : '',
  };
};

// "100, 1.000 2000" -> [100, 1000, 2000]; null = khong doc duoc
function parseMuc(s: string): number[] | null {
  const parts = s.split(/[,;\s]+/).filter(Boolean).map((p) => p.replace(/\./g, ''));
  if (!parts.length || parts.some((p) => !/^\d{1,15}$/.test(p))) return null;
  return parts.map(Number);
}

// Cung khoang voi he B (kiemCms): tra loi theo o, hoac tai lieu CMS khi hop le
type Errors = Partial<Record<string, string>>;
function toDoc(f: Form): { doc: Doc; errors: Errors } {
  const errors: Errors = {};
  const doc: Doc = {};
  const num = (v: string) => Number(v.replace(',', '.'));
  for (const r of ROOMS) {
    const rf = f[r.key];
    const room: Room = {};
    if (rf.rtp) {
      const n = num(rf.rtp);
      if (!(n >= 50 && n <= 99)) errors[r.key + '.rtp'] = 'Từ 50% đến 99%';
      else room.rtp = Number((n / 100).toFixed(4));
    }
    if (rf.muc.trim()) {
      const m = parseMuc(rf.muc);
      if (!m || m.length > 10 || m.some((x) => !Number.isSafeInteger(x) || x <= 0)) errors[r.key + '.muc'] = '1 đến 10 số nguyên dương, cách nhau bởi dấu phẩy';
      else if (m.some((x, i) => i > 0 && x <= m[i - 1]!)) errors[r.key + '.muc'] = 'Phải tăng dần, không trùng';
      else room.mucCuoc = m;
    }
    if (Object.keys(room).length) doc.phong = { ...doc.phong, [r.id]: room };
  }
  const hu: NonNullable<Doc['hu']> = {};
  if (f.phanTram) {
    const n = num(f.phanTram);
    if (!(n >= 0 && n <= 5)) errors.phanTram = 'Từ 0% đến 5%'; else hu.phanTramVaoHu = n;
  }
  if (f.khoiTao) {
    const n = Number(f.khoiTao);
    if (!(Number.isSafeInteger(n) && n >= 0 && n <= 1e12)) errors.khoiTao = 'Số nguyên từ 0 đến 1.000.000.000.000'; else hu.huKhoiTao = n;
  }
  if (f.heSoNo) {
    const n = num(f.heSoNo);
    if (!(n >= 0 && n <= 1)) errors.heSoNo = 'Từ 0 đến 1'; else hu.heSoNo = n;
  }
  if (Object.keys(hu).length) doc.hu = hu;
  const bot: NonNullable<Doc['bot']> = {};
  if (f.botBat) bot.bat = f.botBat === 'bat';
  if (f.botMin || f.botMax) {
    if (!f.botMin || !f.botMax) errors.soBot = 'Chọn cả tối thiểu và tối đa, hoặc để cả hai mặc định';
    else if (Number(f.botMin) > Number(f.botMax)) errors.soBot = 'Tối thiểu lớn hơn tối đa';
    else bot.soBot = [Number(f.botMin), Number(f.botMax)];
  }
  if (Object.keys(bot).length) doc.bot = bot;
  return { doc, errors };
}

function describe(d: Doc) {
  const p: string[] = [];
  for (const r of ROOMS) {
    const x = d.phong?.[r.id];
    p.push(`${r.name}: trả ${x?.rtp != null ? pctOf(x.rtp) + '%' : 'mặc định'}, mức đạn ${x?.mucCuoc ? mucText(x.mucCuoc) : 'mặc định'}`);
  }
  const h = d.hu ?? {};
  p.push(`Hũ: góp ${h.phanTramVaoHu != null ? h.phanTramVaoHu + '%' : 'mặc định'}, khởi tạo ${h.huKhoiTao != null ? formatVnd(h.huKhoiTao) : 'mặc định'}, hệ số nổ ${h.heSoNo ?? 'mặc định'}`);
  const b = d.bot ?? {};
  p.push(`Bot: ${b.bat == null ? 'mặc định' : b.bat ? 'bật' : 'tắt'}, số bot ${b.soBot ? b.soBot.join(' đến ') : 'mặc định'}`);
  return p;
}

const decimal = (v: string) => v.replace(',', '.').replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 10);
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';
// goi y trong o trong: gia tri game dang dung khi he A cho biet
const hint = (v: string | null | undefined) => (v ? v + ' (mặc định)' : 'mặc định');

function Editor({ data, editable }: { data: Data; editable: boolean }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const base = toForm(data.noiDung);
  const [f, setF] = useState<Form>(base);
  const [open, setOpen] = useState(false);
  const [stale, setStale] = useState(false);
  const { doc, errors } = toDoc(f);
  const bad = Object.keys(errors).length > 0;
  const dirty = JSON.stringify(f) !== JSON.stringify(base);
  // o trong = khoa chua dat trong CMS, nen gia tri hieu luc cua no chinh la mac dinh
  const eff = data.hieuLuc;
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/games/ban-ca', { noiDung: doc, expect: data.meta.version }),
    onSuccess: (r) => nav('/commands/' + r.command.id),
    onError: (e) => { if (isStale(e)) { setOpen(false); setStale(true); qc.invalidateQueries({ queryKey: ['ban-ca'] }); } },
  });
  const setRoom = (k: 'r1' | 'r3', p: Partial<RoomForm>) => setF((o) => ({ ...o, [k]: { ...o[k], ...p } }));
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((o) => ({ ...o, [k]: v }));
  const effHu = eff?.hu;
  const effBot = eff?.bot;
  return (
    <>
      {stale ? <InlineNotification kind="warning" lowContrast title="Cấu hình vừa được người khác lưu"
        subtitle="Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại." onClose={() => setStale(false)} /> : null}
      <div className="control-grid">
        {ROOMS.map((r) => {
          const e = eff?.phong?.[r.id];
          return (
            <section key={r.key} className="panel control">
              <div className="panel__head">
                <h2>{r.name} <span className="muted num">phòng {r.id}</span></h2>
                {data.noiDung?.phong?.[r.id] ? <Tag size="sm" type="blue">CMS đặt</Tag> : <Tag size="sm" type="gray">mặc định</Tag>}
              </div>
              <p className="muted">{r.note}</p>
              <fieldset className="control__group" disabled={!editable}>
                <TextInput id={'rtp-' + r.id} size="sm" labelText="Tỷ lệ trả kỳ vọng mỗi viên đạn (%)" inputMode="decimal"
                  placeholder={hint(e?.rtp != null ? pctOf(e.rtp) + '%' : null)} helperText="Từ 50% đến 99%. Để trống = mặc định."
                  value={f[r.key].rtp} onChange={(ev) => setRoom(r.key, { rtp: decimal(ev.target.value) })}
                  invalid={!!errors[r.key + '.rtp']} invalidText={errors[r.key + '.rtp']} />
                <TextInput id={'muc-' + r.id} size="sm" labelText="Mức đạn người chơi chọn được" inputMode="numeric"
                  placeholder={hint(e?.mucCuoc ? mucText(e.mucCuoc) : null)} helperText="1 đến 10 mức tăng dần, cách nhau bởi dấu phẩy. Để trống = mặc định."
                  value={f[r.key].muc} onChange={(ev) => setRoom(r.key, { muc: ev.target.value.replace(/[^\d.,;\s]/g, '').slice(0, 200) })}
                  invalid={!!errors[r.key + '.muc']} invalidText={errors[r.key + '.muc']} />
              </fieldset>
            </section>
          );
        })}
        <section className="panel control">
          <div className="panel__head">
            <h2>Hũ Godzilla</h2>
            {data.noiDung?.hu ? <Tag size="sm" type="blue">CMS đặt</Tag> : <Tag size="sm" type="gray">mặc định</Tag>}
          </div>
          <p className="muted">Chỉ ở phòng Hải Vực Cự Thú. Mỗi viên trúng Godzilla có cơ hội nổ hũ bằng hệ số nổ nhân mức đạn chia mức đạn lớn nhất của phòng.</p>
          <fieldset className="control__group" disabled={!editable}>
            <TextInput id="hu-pt" size="sm" labelText="Phần mỗi viên đạn góp vào hũ (%)" inputMode="decimal"
              placeholder={hint(effHu?.phanTramVaoHu != null ? effHu.phanTramVaoHu + '%' : null)} helperText="Từ 0% đến 5%, trừ vào tỷ lệ trả của phòng VIP."
              value={f.phanTram} onChange={(ev) => set('phanTram', decimal(ev.target.value))} invalid={!!errors.phanTram} invalidText={errors.phanTram} />
            <MoneyInput id="hu-kt" size="sm" labelText="Hũ khởi tạo sau mỗi lần nổ" maxDigits={13}
              placeholder={hint(effHu?.huKhoiTao != null ? formatVnd(effHu.huKhoiTao) : null)}
              value={f.khoiTao || ''} onChange={(v) => set('khoiTao', v)}
              invalid={!!errors.khoiTao} invalidText={errors.khoiTao} />
            <TextInput id="hu-no" size="sm" labelText="Hệ số nổ hũ" inputMode="decimal"
              placeholder={hint(effHu?.heSoNo != null ? String(effHu.heSoNo) : null)} helperText="Từ 0 đến 1. Ví dụ 0.0005: viên đạn mức lớn nhất trúng Godzilla có 0,05% cơ hội nổ hũ. 0 = hũ không nổ."
              value={f.heSoNo} onChange={(ev) => set('heSoNo', decimal(ev.target.value))} invalid={!!errors.heSoNo} invalidText={errors.heSoNo} />
          </fieldset>
        </section>
        <section className="panel control">
          <div className="panel__head">
            <h2>Bot</h2>
            {data.noiDung?.bot ? <Tag size="sm" type="blue">CMS đặt</Tag> : <Tag size="sm" type="gray">mặc định</Tag>}
          </div>
          <p className="muted">Bot chỉ để bàn đông vui: bắn bằng tiền ảo, không vào ví, sổ giao dịch hay hũ.</p>
          <fieldset className="control__group" disabled={!editable}>
            <Select id="bot-bat" size="sm" labelText="Bot ngồi bàn" value={f.botBat} onChange={(ev) => set('botBat', ev.target.value as Form['botBat'])}>
              <SelectItem value="" text={hint(effBot?.bat == null ? null : effBot.bat ? 'Bật' : 'Tắt')} />
              <SelectItem value="bat" text="Bật" />
              <SelectItem value="tat" text="Tắt" />
            </Select>
            <div className="row">
              {(['botMin', 'botMax'] as const).map((k, i) => (
                <Select key={k} id={'bot-' + k} size="sm" labelText={i === 0 ? 'Số bot mỗi bàn, tối thiểu' : 'Tối đa'} value={f[k]}
                  onChange={(ev) => set(k, ev.target.value)} invalid={!!errors.soBot && i === 1} invalidText={errors.soBot}>
                  <SelectItem value="" text={hint(effBot?.soBot ? String(effBot.soBot[i]) : null)} />
                  {[0, 1, 2, 3].map((n) => <SelectItem key={n} value={String(n)} text={String(n)} />)}
                </Select>
              ))}
            </div>
            <p className="muted note">Bàn có 4 ghế; bot nhường ghế cho người chơi thật.</p>
          </fieldset>
        </section>
      </div>
      {editable ? (
        <div className="row section">
          <Button kind="ghost" disabled={!dirty} onClick={() => setF(base)}>Bỏ thay đổi</Button>
          <Button disabled={!dirty || bad || data.pending.length > 0} onClick={() => setOpen(true)}>Lưu, áp dụng ngay</Button>
        </div>
      ) : null}
      <Modal open={open} modalHeading="Đổi cấu hình Bắn Cá" primaryButtonText="Áp dụng ngay" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <p>Thay đổi này đổi số tiền trả cho người chơi. Hệ A áp dụng ngay, không chờ người duyệt; game dùng cấu hình mới từ viên đạn kế tiếp.</p>
          <ul className="list">{describe(doc).map((x) => <li key={x}>{x}</li>)}</ul>
          <InlineError error={m.error} />
        </div>
      </Modal>
    </>
  );
}

export function FishConfigPage() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['ban-ca'], queryFn: () => api.get<Data>('/api/admin/games/ban-ca') });
  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { meta, pending, last } = q.data;
  return (
    <div className="page">
      <PageHeader title="Bắn Cá"
        lead="Tỷ lệ trả và mức đạn của hai phòng, hũ Godzilla của phòng VIP và bot. Ô để trống thì game dùng mặc định. Hệ số trả của từng loại cá giữ theo bảng trong game." />
      <div className="row health">
        <span>Cấu hình CMS: {meta.source === 'default' ? <span className="muted">chưa đặt, game dùng mặc định</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
      </div>
      {last?.status === 'FAILED' ? (
        <InlineNotification kind="error" lowContrast hideCloseButton title="Lệnh gần nhất bị hệ A từ chối"
          subtitle={[(last.lastError && HE_A_ERRORS[last.lastError]) || last.lastError, last.chiTiet].filter(Boolean).join(': ')} />
      ) : null}
      {pending.length ? (
        <>
          <InlineNotification kind="info" lowContrast hideCloseButton title={`${pending.length} lệnh đang chờ`}
            subtitle="Đợi lệnh đang gửi xong, hoặc duyệt / từ chối lệnh còn chờ duyệt từ trước, rồi mới tạo lệnh mới: lệnh tạo trên phiên bản cũ sẽ bị hệ A từ chối." />
          <ul className="list pending-list">
            {pending.map((p) => (
              <li key={p.id}><CommandStatus s={p.status} /><Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span></li>
            ))}
          </ul>
        </>
      ) : null}
      <Editor key={meta.version} data={q.data} editable={can('games.config')} />
    </div>
  );
}
